import React, { useState, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import { TemplateField, TemplateSchema, FieldOption } from '../types/template';

interface Props {
    schema: TemplateSchema;
    initialAnswers?: Record<string, unknown>;
    readOnly?: boolean;
    lockedFields?: Record<string, unknown>; // field id → value; pre-filled and non-editable
    onSubmit?: (answers: Record<string, unknown>) => void;
    submitting?: boolean;
    submitLabel?: string;
    currentLang?: string;
}

// ── Section grouping ───────────────────────────────────────────────────────────
// Fields whose label contains " · " are grouped by the prefix before that separator.
// e.g. "1/6 - 5/6 · Monday" groups under "1/6 - 5/6" with display label "Monday".
type FieldWithDisplay = TemplateField & { displayLabel: string };
type Block = { prefix: string | null; fields: FieldWithDisplay[] };

function groupByPrefix(fields: TemplateField[], getLabel: (f: TemplateField) => string): Block[] {
    const blocks: Block[] = [];
    for (const field of fields) {
        const lbl = getLabel(field);
        const sep = lbl.indexOf(' · ');
        if (sep > -1) {
            const prefix = lbl.slice(0, sep);
            const suffix = lbl.slice(sep + 3);
            const last   = blocks[blocks.length - 1];
            if (last?.prefix === prefix) {
                last.fields.push({ ...field, displayLabel: suffix });
            } else {
                blocks.push({ prefix, fields: [{ ...field, displayLabel: suffix }] });
            }
        } else {
            blocks.push({ prefix: null, fields: [{ ...field, displayLabel: lbl }] });
        }
    }
    return blocks;
}

// ──────────────────────────────────────────────────────────────────────────────

export default function FormRenderer({
    schema,
    initialAnswers = {},
    readOnly = false,
    lockedFields = {},
    onSubmit,
    submitting = false,
    submitLabel = 'Submit',
    currentLang = 'en',
}: Props) {
    const gl = (field: TemplateField) =>
        field.labels?.[currentLang] || field.labels?.['en'] || field.label;
    const gol = (opt: FieldOption) =>
        opt.labels?.[currentLang] || opt.labels?.['en'] || opt.label;
    const gp = (field: TemplateField) =>
        field.placeholders?.[currentLang] || field.placeholders?.['en'] || field.placeholder;
    const [answers, setAnswers] = useState<Record<string, unknown>>({ ...initialAnswers, ...lockedFields });
    const [errors,  setErrors]  = useState<Record<string, string>>();
    const [shaking, setShaking] = useState(false);

    useEffect(() => {
        setAnswers({ ...initialAnswers, ...lockedFields });
    }, [initialAnswers]); // eslint-disable-line

    const isLocked = (id: string) => id in lockedFields;

    const setVal = (id: string, val: unknown) => {
        if (isLocked(id)) return;
        setAnswers(prev => ({ ...prev, [id]: val }));
    };

    const validate = (): boolean => {
        const errs: Record<string, string> = {};
        for (const f of schema.fields) {
            if (!f.required) continue;
            const v = answers[f.id];
            if (v === undefined || v === null || v === '' ||
                (Array.isArray(v) && v.length === 0)) {
                errs[f.id] = 'This field is required';
            }
            if (f.type === 'textbox' && f.inputType === 'email' && typeof v === 'string' && v) {
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v))
                    errs[f.id] = 'Please enter a valid email address';
            }
            if (f.type === 'textbox' && f.inputType === 'phone' && typeof v === 'string' && v) {
                if (!/^[0-9+\-()\s]{6,20}$/.test(v))
                    errs[f.id] = 'Please enter a valid phone number';
            }
            if (f.type === 'textbox' && f.inputType === 'number' && typeof v === 'string' && v) {
                const n = Number(v);
                if (isNaN(n))
                    errs[f.id] = 'Please enter a valid number';
                else if (f.minValue !== undefined && n < f.minValue)
                    errs[f.id] = `Value must be at least ${f.minValue}`;
                else if (f.maxValue !== undefined && n > f.maxValue)
                    errs[f.id] = `Value must be at most ${f.maxValue}`;
            }
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!onSubmit) return;
        if (validate()) {
            onSubmit(answers);
        } else {
            // Shake animation + scroll to first error
            setShaking(true);
            setTimeout(() => setShaking(false), 500);
            const sortedForScroll = schema.fields.slice().sort((a, b) => a.order - b.order);
            const firstErrField = sortedForScroll.find(f => {
                const v = answers[f.id];
                return f.required && (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0));
            });
            if (firstErrField) {
                document.getElementById(`field-${firstErrField.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    };

    // ── Render a single field input (used in both grouped rows and standalone fields) ──
    const renderInput = (field: TemplateField) => {
        const val    = answers[field.id];
        const locked = isLocked(field.id);

        switch (field.type) {
            case 'textbox':
                return (
                    <div style={{ position: 'relative' }}>
                        <Form.Control
                            type={field.inputType === 'phone' ? 'tel' : field.inputType ?? 'text'}
                        inputMode={field.inputType === 'number' ? 'numeric' : undefined}
                        min={field.inputType === 'number' && field.minValue !== undefined ? field.minValue : undefined}
                        max={field.inputType === 'number' && field.maxValue !== undefined ? field.maxValue : undefined}
                            placeholder={gp(field) ?? ''}
                            value={(val as string) ?? ''}
                            onChange={e => setVal(field.id, e.target.value)}
                            disabled={readOnly || locked}
                            isInvalid={!!errors?.[field.id]}
                            style={locked ? { paddingRight: 100, background: '#f8f9fa', color: '#495057' } : undefined}
                        />
                        {locked && (
                            <span style={{
                                position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                                fontSize: 11, fontWeight: 600, color: '#6c757d',
                                background: '#e9ecef', borderRadius: 20, padding: '2px 8px',
                                pointerEvents: 'none',
                            }}>Auto-filled</span>
                        )}
                    </div>
                );

            case 'comment_box':
                return (
                    <Form.Control
                        as="textarea" rows={4}
                        placeholder={gp(field) ?? ''}
                        value={(val as string) ?? ''}
                        onChange={e => setVal(field.id, e.target.value)}
                        disabled={readOnly || locked}
                        isInvalid={!!errors?.[field.id]}
                    />
                );

            case 'single_choice':
            case 'multiple_choice': {
                const opts   = field.options ?? [];
                const inline = opts.length <= 3;
                return (
                    <div className={inline ? 'd-flex flex-wrap gap-3' : ''}>
                        {opts.map(opt => (
                            <Form.Check
                                key={opt.id}
                                type={field.type === 'single_choice' ? 'radio' : 'checkbox'}
                                id={`${field.id}-${opt.id}`}
                                name={field.id}
                                label={gol(opt)}
                                disabled={readOnly}
                                checked={
                                    field.type === 'single_choice'
                                        ? val === opt.id
                                        : Array.isArray(val) && (val as string[]).includes(opt.id)
                                }
                                onChange={() => {
                                    if (field.type === 'single_choice') {
                                        setVal(field.id, opt.id);
                                    } else {
                                        const prev = Array.isArray(val) ? val as string[] : [];
                                        setVal(field.id, prev.includes(opt.id)
                                            ? prev.filter(x => x !== opt.id)
                                            : [...prev, opt.id]);
                                    }
                                }}
                            />
                        ))}
                    </div>
                );
            }

            case 'checkboxes':
                return (
                    <div>
                        {(field.options ?? []).map(opt => (
                            <Form.Check
                                key={opt.id} type="checkbox"
                                id={`${field.id}-${opt.id}`}
                                label={gol(opt)}
                                disabled={readOnly}
                                checked={Array.isArray(val) && (val as string[]).includes(opt.id)}
                                onChange={() => {
                                    const prev = Array.isArray(val) ? val as string[] : [];
                                    setVal(field.id, prev.includes(opt.id)
                                        ? prev.filter(x => x !== opt.id)
                                        : [...prev, opt.id]);
                                }}
                            />
                        ))}
                    </div>
                );

            case 'dropdown':
                return (
                    <Form.Select
                        value={(val as string) ?? ''}
                        onChange={e => setVal(field.id, e.target.value)}
                        disabled={readOnly}
                        isInvalid={!!errors?.[field.id]}
                    >
                        <option value="">— Select —</option>
                        {(field.options ?? []).map(opt => (
                            <option key={opt.id} value={opt.id}>{gol(opt)}</option>
                        ))}
                    </Form.Select>
                );

            case 'star_rating': {
                const max    = field.maxStars ?? 5;
                const rating = (val as number) ?? 0;
                return (
                    <div className="star-rating" style={{ touchAction: 'manipulation' }}>
                        {Array.from({ length: max }, (_, i) => i + 1).map(n => (
                            <span
                                key={n}
                                className={`star-rating__star${rating >= n ? ' star-rating__star--filled' : ''}`}
                                onClick={() => !readOnly && setVal(field.id, n)}
                                role={readOnly ? undefined : 'button'}
                                aria-label={`${n} star`}
                                style={{ touchAction: 'manipulation' }}
                            >★</span>
                        ))}
                    </div>
                );
            }

            case 'image_upload':
                if (readOnly) return <div className="text-muted fst-italic">Image upload field</div>;
                return (
                    <Form.Control
                        type="file"
                        accept="image/*"
                        disabled={readOnly}
                        style={{ fontSize: 16 }}
                        onChange={e => {
                            const file = (e.target as HTMLInputElement).files?.[0];
                            if (file) setVal(field.id, file.name);
                        }}
                    />
                );

            case 'image_choice':
                return (
                    <div className="image-choice-grid">
                        {(field.options ?? []).map(opt => (
                            <div
                                key={opt.id}
                                className={`image-choice-grid__item${val === opt.id ? ' image-choice-grid__item--selected' : ''}`}
                                onClick={() => !readOnly && setVal(field.id, opt.id)}
                                role={readOnly ? undefined : 'button'}
                                style={{ touchAction: 'manipulation' }}
                            >
                                {opt.imageUrl
                                    ? <img src={opt.imageUrl} alt={gol(opt)} />
                                    : <div style={{ height: 80, background: '#f0f2f5', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#adb5bd', fontSize: 12 }}>No image</div>
                                }
                                <div style={{ fontSize: 12 }}>{gol(opt)}</div>
                            </div>
                        ))}
                    </div>
                );

            default:
                return <div className="text-muted">[{field.type}]</div>;
        }
    };

    // ── Grouped section row (e.g. "Monday  [Yes] [No]") ──────────────────────
    const renderGroupedRow = (field: FieldWithDisplay) => {
        const opts   = field.options ?? [];
        const usePills = opts.length <= 4; // pill buttons for small option sets
        const val    = answers[field.id];
        const err    = errors?.[field.id];
        const locked = isLocked(field.id);

        const toggle = (optId: string) => {
            if (readOnly || locked) return;
            if (field.type === 'single_choice') {
                setVal(field.id, optId);
            } else {
                const prev = Array.isArray(val) ? val as string[] : [];
                setVal(field.id, prev.includes(optId)
                    ? prev.filter(x => x !== optId)
                    : [...prev, optId]);
            }
        };

        return (
            <div
                key={field.id}
                id={`field-${field.id}`}
                className={`tfr-group-row${err ? ' tfr-group-row--error' : ''}`}
            >
                <div className="tfr-group-row__label">
                    {field.displayLabel}
                    {field.required && <span className="template-form-renderer__required"> *</span>}
                </div>

                {usePills ? (
                    <div className="tfr-group-row__pills">
                        {opts.map(opt => {
                            const selected = field.type === 'single_choice'
                                ? val === opt.id
                                : Array.isArray(val) && (val as string[]).includes(opt.id);
                            return (
                                <button
                                    key={opt.id}
                                    type="button"
                                    className={`tfr-pill${selected ? ' tfr-pill--selected' : ''}${readOnly || locked ? ' tfr-pill--disabled' : ''}`}
                                    onClick={() => toggle(opt.id)}
                                    style={{ touchAction: 'manipulation' }}
                                >
                                    {gol(opt)}
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    <div className="tfr-group-row__options">
                        {opts.map(opt => (
                            <Form.Check
                                key={opt.id}
                                type={field.type === 'single_choice' ? 'radio' : 'checkbox'}
                                id={`${field.id}-${opt.id}`}
                                name={field.id}
                                label={gol(opt)}
                                disabled={readOnly || locked}
                                checked={
                                    field.type === 'single_choice'
                                        ? val === opt.id
                                        : Array.isArray(val) && (val as string[]).includes(opt.id)
                                }
                                onChange={() => toggle(opt.id)}
                            />
                        ))}
                    </div>
                )}

                {err && <div className="invalid-feedback d-block w-100 mt-1">{err}</div>}
            </div>
        );
    };

    // ── Stats ──────────────────────────────────────────────────────────────────
    const sortedFields = schema.fields.slice().sort((a, b) => a.order - b.order);
    const totalFields  = sortedFields.length;
    const filledCount  = sortedFields.filter(f => {
        const v = answers[f.id];
        return v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && (v as unknown[]).length === 0);
    }).length;
    const blocks = groupByPrefix(sortedFields, gl);

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <form onSubmit={handleSubmit} className={`template-form-renderer${shaking ? ' template-form-renderer--shake' : ''}`}>
            {totalFields > 0 && (
                <div className="template-form-renderer__progress mb-4">
                    <div className="d-flex justify-content-between mb-1" style={{ fontSize: 12, color: '#6c757d' }}>
                        <span>{filledCount} of {totalFields} answered</span>
                        <span>{Math.round((filledCount / totalFields) * 100)}%</span>
                    </div>
                    <div className="progress" style={{ height: 4, borderRadius: 2 }}>
                        <div
                            className="progress-bar"
                            style={{ width: `${(filledCount / totalFields) * 100}%`, background: '#c5281f', transition: 'width 0.3s ease' }}
                        />
                    </div>
                </div>
            )}

            {blocks.map((block, bi) =>
                block.prefix !== null ? (
                    // ── Grouped section card ────────────────────────────────
                    <div key={`${block.prefix}-${bi}`} className="tfr-section">
                        <div className="tfr-section__header">{block.prefix}</div>
                        <div className="tfr-section__body">
                            {block.fields.map(f => renderGroupedRow(f))}
                        </div>
                    </div>
                ) : (
                    // ── Standalone field ────────────────────────────────────
                    block.fields.map(field => (
                        <div key={field.id} id={`field-${field.id}`} className={`template-form-renderer__field${errors?.[field.id] ? ' template-form-renderer__field--error' : ''}`}>
                            <div className="template-form-renderer__label">
                                {gl(field) || <span className="text-muted fst-italic">Untitled question</span>}
                                {field.required && <span className="template-form-renderer__required"> *</span>}
                            </div>
                            {renderInput(field)}
                            {errors?.[field.id] && (
                                <div className="invalid-feedback d-block">{errors[field.id]}</div>
                            )}
                        </div>
                    ))
                )
            )}

            {!readOnly && onSubmit && (
                <div className="mt-4">
                    <Button
                        type="submit"
                        style={{ background: '#c5281f', border: 'none', minWidth: 140 }}
                        disabled={submitting}
                    >
                        {submitting ? 'Submitting…' : submitLabel}
                    </Button>
                </div>
            )}
        </form>
    );
}
